/**
 * Home's setup guide: the Loot Boxes plan (read from `status.billing`), then the four placements,
 * read from App Bridge's `shopify.app.extensions()`: the theme app extension's embed and its drop
 * table block as placed on the published theme, and the reveal UI extension's two targets, the
 * Thank-you page on the published checkout profile and the Order status page. No access scope is
 * needed. Creating a box waits on all five (`createBoxBlocker`, `boxSale.ts`).
 */

/** The part of `shopify.app.extensions()` this reads; anything else it answers is ignored. */
export interface AppExtensionInfo {
  handle: string;
  type: string;
  activations: {
    /** A UI extension's target (`purchase.thank-you.block.render`). */
    target?: string;
    /** A theme app extension's block or embed, by its file name. */
    handle?: string;
    status?: string;
  }[];
}

/** The guide's steps, in the order it lists them. */
export const SETUP_STEPS = [
  "plan",
  "appEmbed",
  "dropTableBlock",
  "thankYouBlock",
  "orderStatusBlock",
] as const;
export type SetupStepKey = (typeof SETUP_STEPS)[number];

/** The steps Shopify reports as placements. */
export type PlacementStepKey = Exclude<SetupStepKey, "plan">;
export const PLACEMENT_STEPS = SETUP_STEPS.filter((key): key is PlacementStepKey => key !== "plan");

/**
 * What Shopify answered about the placements: each one's state, or `"pending"` until it answers,
 * or `"failed"` when it could not, which the guide shows as every placement still to do.
 */
export type PlacementsRead = Record<PlacementStepKey, boolean> | "pending" | "failed";

/** Each step's title in the guide, which Boxes also names when a step still stops a new box. */
export const SETUP_STEP_TITLES: Record<SetupStepKey, string> = {
  plan: "Approve the Loot Boxes plan",
  appEmbed: "Turn on the Loot Boxes app embed",
  dropTableBlock: "Show the drop table on your product pages",
  thankYouBlock: "Add the reveal button to your Thank-you page",
  orderStatusBlock: "Add the reveal button to your Order status page",
};

/** The theme embed's file name (`extensions/loot-theme/blocks/app-embed.liquid`). */
const APP_EMBED_HANDLE = "app-embed";
/**
 * The product page drop table block's file name
 * (`extensions/loot-theme/blocks/loot-droptable.liquid`). Its schema enables it on product
 * templates only, so active means on a product page.
 */
export const DROP_TABLE_BLOCK_HANDLE = "loot-droptable";
/** The reveal extension's handle (`extensions/loot-thank-you`) and its two targets. */
const REVEAL_EXTENSION_HANDLE = "loot-thank-you";
const THANK_YOU_TARGET = "purchase.thank-you.block.render";
/** The Order status page, which "View your order" in Shopify's order emails opens. */
const ORDER_STATUS_TARGET = "customer-account.order-status.block.render";

function revealBlockPlaced(extensions: AppExtensionInfo[], target: string): boolean {
  return extensions.some(
    (extension) =>
      extension.type === "ui_extension" &&
      extension.handle === REVEAL_EXTENSION_HANDLE &&
      extension.activations.some((a) => a.target === target),
  );
}

function themeBlockActive(extensions: AppExtensionInfo[], blockHandle: string): boolean {
  return extensions.some(
    (extension) =>
      extension.type === "theme_app_extension" &&
      extension.activations.some((a) => a.handle === blockHandle && a.status === "active"),
  );
}

/**
 * Each placement's state: done when Shopify reports it active. The Loot box block is no step (the
 * merchant places box products where they like), so it is not read; the drop table block is one,
 * since a shopper should see what a box holds before buying it. The reveal block is two steps, one
 * per page, so placing it on the Thank-you page alone never reads as both.
 */
export function placementStepsDone(
  extensions: AppExtensionInfo[],
): Record<PlacementStepKey, boolean> {
  return {
    appEmbed: themeBlockActive(extensions, APP_EMBED_HANDLE),
    dropTableBlock: themeBlockActive(extensions, DROP_TABLE_BLOCK_HANDLE),
    thankYouBlock: revealBlockPlaced(extensions, THANK_YOU_TARGET),
    orderStatusBlock: revealBlockPlaced(extensions, ORDER_STATUS_TARGET),
  };
}

/**
 * The step the guide shows open, one at a time: the one the merchant opened, none once they
 * closed it (`"none"`), and otherwise the first step not done; none once every step is.
 */
export function openSetupStep(
  done: Record<SetupStepKey, boolean>,
  chosen: SetupStepKey | "none" | null,
): SetupStepKey | null {
  if (chosen === "none") return null;
  return chosen ?? SETUP_STEPS.find((key) => !done[key]) ?? null;
}

/**
 * How much of the guide Home draws: every step while one is not done, whatever was dismissed
 * before (a plan cancelled or an embed switched off later brings it back); the one "Store set up"
 * line once all are; nothing once that line is dismissed.
 */
export function setupGuideView(
  done: Record<SetupStepKey, boolean>,
  dismissed: boolean,
): "steps" | "folded" | "hidden" {
  if (!SETUP_STEPS.every((key) => done[key])) return "steps";
  return dismissed ? "hidden" : "folded";
}

/** The browser's memory of a dismissed "Store set up" line, per shop. */
function dismissedKey(shopDomain: string): string {
  return `loot:setup-done-dismissed:${shopDomain}`;
}

/**
 * Whether the merchant dismissed the "Store set up" line in this browser. A convenience only:
 * storage that is blocked or cleared just shows the line again.
 */
export function isSetupDoneDismissed(shopDomain: string): boolean {
  try {
    return localStorage.getItem(dismissedKey(shopDomain)) === "1";
  } catch {
    return false;
  }
}

export function dismissSetupDone(shopDomain: string): void {
  try {
    localStorage.setItem(dismissedKey(shopDomain), "1");
  } catch {
    // Blocked storage: the line is hidden until the next load.
  }
}
